/* AmbientMusicButton — small calm play/pause button for soft background
   music on the home page and memorial pages. Attempts autoplay when
   `autoPlay` is true (e.g. QR scan landing); falls back to a pulsing
   prompt when the browser blocks the play() promise (iOS Safari, first-
   time Chrome desktop). Singleton audio element so navigating between
   pages does not stack instances.

   iter63 — Audio truthful-state rewrite.
   The previous implementation set UI state to "playing" on the `play`
   event, which fires the moment `audio.play()` is called — *before* any
   audio data has actually been decoded. That meant the indicator could
   say "Music · On" while the browser silently failed to fetch or decode
   the source. We now drive the UI from the `playing` event (real audio
   coming out of the speaker) and surface `error`/`stalled` as a clear
   "couldn't play" toast + a Tap-to-retry pulse. */
import React, { useEffect, useRef, useState } from 'react';
import { Music, Pause } from 'lucide-react';
import { toast } from 'sonner';
import { registerAudioSource, claimAudioFocus } from '../lib/audioBus';

let __ambient_audio = null;        // singleton across renders
let __ambient_subscribers = new Set();
let __ambient_error_toast_for = null;  // src for which we've already toasted

function notify(state) {
  for (const fn of __ambient_subscribers) {
    try { fn(state); } catch { /* noop */ }
  }
}

function ensureAudio(src) {
  if (!__ambient_audio) {
    __ambient_audio = new Audio();
    __ambient_audio.loop = true;
    // 0.55 — comfortably audible on phone speakers without overpowering
    // narration / on-page voice. Previously 0.16 (inaudible) and then
    // 0.45 (still quiet on some Android devices).
    __ambient_audio.volume = 0.55;
    __ambient_audio.preload = 'auto';
    // NOTE: do NOT set crossOrigin = 'use-credentials' here. Doing so
    // forces the browser to validate `Access-Control-Allow-Origin` and
    // refuse the load when the server returns `*` (our CORS default).
    // Audio playback works fine without it on same-origin, and Bearer/
    // cookie auth still flows through normally.
    __ambient_audio.addEventListener('playing', () => notify('playing'));
    // Audio focus: the moment ambient starts, silence every other
    // memorial audio source (video, narration, voice memories).
    __ambient_audio.addEventListener('play', () => claimAudioFocus('ambient-music'));
    registerAudioSource('ambient-music', () => {
      if (__ambient_audio && !__ambient_audio.paused) __ambient_audio.pause();
    });
    __ambient_audio.addEventListener('pause',   () => notify('paused'));
    __ambient_audio.addEventListener('ended',   () => notify('paused'));
    __ambient_audio.addEventListener('emptied', () => notify('paused'));
    __ambient_audio.addEventListener('waiting', () => notify('loading'));
    __ambient_audio.addEventListener('stalled', () => notify('loading'));
    __ambient_audio.addEventListener('error', () => {
      notify('error');
      // Browser-level error (404, decode, network) — surface it once per
      // src so the UI never lies about playback succeeding.
      const failedSrc = __ambient_audio?.currentSrc || __ambient_audio?.src;
      if (failedSrc && __ambient_error_toast_for !== failedSrc) {
        __ambient_error_toast_for = failedSrc;
        toast.error('This soundtrack could not be played. Try another piece.');
      }
    });
  }
  if (src && __ambient_audio.src !== src) {
    __ambient_audio.src = src;
    __ambient_error_toast_for = null;   // new source — allow a fresh toast
    // Safari requires an explicit load() after a src change for the new
    // file to actually start fetching.
    try { __ambient_audio.load(); } catch (_e) { /* noop */ }
  }
  return __ambient_audio;
}

export function AmbientMusicButton({ src, label = 'Ambient music', autoPlay = false }) {
  const [state, setState] = useState('paused');
  // 'needs_tap' surfaces a pulsing prompt when the browser blocked
  // autoplay. Cleared the moment real audio is detected.
  const [needsTap, setNeedsTap] = useState(false);
  const subRef = useRef(null);
  const attemptedRef = useRef(false);

  useEffect(() => {
    subRef.current = (s) => {
      setState(s);
      if (s === 'playing') setNeedsTap(false);
    };
    __ambient_subscribers.add(subRef.current);
    // Reflect current playback in case a prior page started it
    if (__ambient_audio && !__ambient_audio.paused && __ambient_audio.currentTime > 0) {
      setState('playing');
    }
    return () => __ambient_subscribers.delete(subRef.current);
  }, []);

  // Autoplay attempt — runs once when `src` becomes available.
  // The play() promise rejects when the browser blocks it; we catch
  // and switch to the "tap to play" prompt instead of failing silently.
  useEffect(() => {
    if (!autoPlay || !src || attemptedRef.current) return;
    attemptedRef.current = true;
    const a = ensureAudio(src);
    if (!a.paused && a.currentTime > 0) return; // already playing
    const p = a.play();
    if (p && typeof p.then === 'function') {
      p.then(() => {
        // Autoplay attempt accepted — the `playing` event will fire
        // when real audio reaches the speaker and the UI will update.
      }).catch(() => {
        // Most likely browser autoplay policy. Show the prompt.
        setNeedsTap(true);
      });
    }
  }, [autoPlay, src]);

  if (!src) return null;

  const toggle = () => {
    const a = ensureAudio(src);
    if (a.paused) {
      const p = a.play();
      if (p && typeof p.then === 'function') {
        p.catch(() => setNeedsTap(true));
      }
    } else {
      a.pause();
    }
    setNeedsTap(false);
  };

  const playing = state === 'playing';
  const loading = state === 'loading';
  const showPulse = needsTap && !playing;
  return (
    <button
      type="button"
      onClick={toggle}
      data-testid="ambient-music-toggle"
      aria-label={playing ? `Pause ${label}` : `Play ${label}`}
      title={playing ? `Pause ${label}` : (showPulse ? 'Tap to play music' : `Play ${label}`)}
      className={
        'fixed z-30 flex items-center gap-2 px-3 py-2 rounded-full ' +
        'bottom-4 left-4 sm:bottom-auto sm:left-auto sm:top-[88px] sm:right-6 ' +
        'border bg-[hsl(var(--surface))]/90 backdrop-blur-sm shadow-sm ' +
        'transition-all duration-300 ' +
        (playing
          ? 'border-[hsl(var(--accent))] text-[hsl(var(--accent))]'
          : showPulse
            ? 'border-[hsl(var(--accent))] text-[hsl(var(--accent))] animate-pulse shadow-[0_0_0_4px_rgba(197,160,89,0.18)]'
            : 'border-[hsl(var(--primary)/0.18)] text-[hsl(var(--primary)/0.7)] hover:text-[hsl(var(--primary))] hover:border-[hsl(var(--primary)/0.4)]')
      }
    >
      {playing ? (
        <Pause size={13} strokeWidth={2} />
      ) : (
        <Music size={13} strokeWidth={1.5} />
      )}
      <span className="text-[10px] tracking-[0.22em] uppercase font-medium">
        {playing
          ? 'Music · On'
          : loading
            ? 'Loading…'
            : (showPulse ? 'Tap to play' : 'Music')}
      </span>
    </button>
  );
}

export default AmbientMusicButton;
